import { untrack, type Accessor } from 'solid-js'
import { intlLocale, platform } from '~/lib/params'
import { preloadImage } from '~/lib/preload-image'
import { m } from '~/paraglide/messages'
import type {
  FirstPurchaseBonusOf,
  TimeCrystalProduct,
} from '~/services/currency-catalogue'
import type { ShopBalances } from '~/store/session-ledger'
import type { CurrencyArtworkStatus } from './currency-artwork'
import {
  CurrencyRewardReveal,
  type RevealedFirstPurchaseBonus,
} from './currency-reward-reveal'
import {
  captureSubmittedAmount,
  iapPurchaseAdapter,
} from './purchase-flow/adapters'
import { PurchaseFlow } from './purchase-flow/surface'

const numberFormatter = new Intl.NumberFormat(intlLocale)

export function TimeCrystalPurchaseFlow(props: {
  artworkStatus: () => CurrencyArtworkStatus
  balances: Accessor<ShopBalances | undefined>
  credit: (currency: keyof ShopBalances, amount: number) => void
  firstPurchaseBonusOf: FirstPurchaseBonusOf
  onArtworkError: () => void
  onArtworkLoad: () => void
  onCancel: () => void
  // The purchase completed: it spends the pack's First-purchase bonus.
  // carriedBonus: whether it was submitted with one.
  onCommitted: (carriedBonus: boolean) => void
  onComplete: () => void
  product: TimeCrystalProduct
  userId: number
}) {
  // Whether the purchase carries the pack's First-purchase bonus is decided
  // as it is submitted (GLOSSARY.md "First-purchase bonus (首購翻倍)"): the
  // backend applies it, and the purchase names no offer. 0: none.
  const { adapter, submittedAmount: submittedFirstPurchaseBonus } =
    captureSubmittedAmount(
      iapPurchaseAdapter({
        closeTransactionFailureEvent:
          'shop_time_crystal_close_transaction_failed',
        deliveryTimeoutEvent: 'shop_time_crystal_delivery_timeout',
        platform,
        product: () => props.product,
        userId: () => props.userId,
      }),
      () => untrack(() => props.firstPurchaseBonusOf(props.product) ?? 0),
    )
  // The confirmation opens with the flow: a bonus pack's opening warms its
  // First-purchase bonus art (GLOSSARY.md "First-purchase bonus art") for
  // the reveal. A failed preload goes unreported — the reveal reports its
  // own load.
  const bonusArtUrl = () => props.product.firstPurchaseBonus?.imageUrl ?? null
  untrack(() => {
    const url = bonusArtUrl()
    if (url && props.firstPurchaseBonusOf(props.product) !== null) {
      void preloadImage(url).catch(() => {})
    }
  })
  const revealedFirstPurchaseBonus = (): RevealedFirstPurchaseBonus | null => {
    const amount = submittedFirstPurchaseBonus()
    return amount ? { amount, imageUrl: bonusArtUrl() } : null
  }
  return (
    <PurchaseFlow
      adapter={adapter}
      confirmation={{
        cta: props.product.priceLocalized,
        // States what the purchase grants: grant + any active bonus.
        description: m.purchase_confirm_in_app_purchase({
          number: numberFormatter.format(
            props.product.grant +
              (props.firstPurchaseBonusOf(props.product) ?? 0),
          ),
          price: props.product.priceLocalized ?? '',
        }),
        title: m.dialog_confirm_purchase_title(),
      }}
      onCancel={() => props.onCancel()}
      onCommitted={() => props.onCommitted(submittedFirstPurchaseBonus() > 0)}
      reveal={() => (
        <CurrencyRewardReveal
          artworkStatus={props.artworkStatus}
          balanceTestIdPrefix="time-crystal-purchase-"
          balances={props.balances}
          onArtworkError={props.onArtworkError}
          onArtworkLoad={props.onArtworkLoad}
          firstPurchaseBonus={revealedFirstPurchaseBonus()}
          onArrival={(amount) => {
            props.credit('gem', amount)
            props.onComplete()
          }}
          product={props.product}
          testIdPrefix="time-crystal-reward"
        />
      )}
      testIdPrefix="time-crystal-purchase"
    />
  )
}
